import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc,
  deleteDoc,
  addDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Order, CreateOrderData, OrderStatus } from '../types/order';
import toast from 'react-hot-toast';

export const useOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    const ordersRef = collection(db, 'orders');
    const q = query(
      ordersRef,
      where('organizationId', '==', user.organizationId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ordersData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
            updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
          } as Order;
        });
        setOrders(ordersData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching orders:', err);
        setError('Hiba a megrendelések betöltésekor');
        setLoading(false);
        toast.error('Hiba a megrendelések betöltésekor');
      }
    );

    return () => unsubscribe();
  }, [user?.organizationId]);

  const createOrder = async (orderData: CreateOrderData): Promise<Order | null> => {
    if (!user?.organizationId) {
      toast.error('Nincs szervezet kiválasztva');
      return null;
    }

    try {
      const now = new Date();
      const newOrder = {
        ...orderData,
        organizationId: user.organizationId,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        createdBy: user.uid
      };

      const docRef = await addDoc(collection(db, 'orders'), newOrder);
      
      const createdOrder: Order = {
        ...orderData,
        id: docRef.id,
        organizationId: user.organizationId,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
        createdBy: user.uid
      };

      toast.success('Megrendelés sikeresen létrehozva');
      return createdOrder;
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Hiba a megrendelés létrehozásakor');
      return null;
    }
  };

  const updateOrder = async (orderId: string, updates: Partial<Order>): Promise<boolean> => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date())
      });
      
      toast.success('Megrendelés sikeresen frissítve');
      return true;
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Hiba a megrendelés frissítésekor');
      return false;
    }
  };

  const updateOrderStatus = async (orderId: string, status: OrderStatus): Promise<boolean> => {
    return updateOrder(orderId, { status });
  };

  const deleteOrder = async (orderId: string): Promise<boolean> => {
    try {
      await deleteDoc(doc(db, 'orders', orderId));
      toast.success('Megrendelés sikeresen törölve');
      return true;
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Hiba a megrendelés törlésekor');
      return false;
    }
  };

  return {
    orders,
    loading,
    error,
    createOrder,
    updateOrder,
    updateOrderStatus,
    deleteOrder
  };
}; 